import React from "react"
import styled, { css } from "styled-components"
// import { graphql } from "gatsby"

import Reveal from "../../utils/Reveal"

import Box from "../../atoms/Box"
import ImageOrVideo from "../../atoms/ImageOrVideo"
import LayoutChanger from "../../atoms/LayoutChanger"
import StandardPadding from "../../atoms/StandardPadding"
import { breakpoints } from "../../../lib/styled-system/constants"
import { hasImageOrVideo as hasRenderableMedia } from "../../sections/ImageText/media"

// function to generate the media query for a given breakpoint index
const set16By9AspectRatioForBreakpoint = breakPoint => `
  @media (max-width: ${breakPoint}) {
    [class^="Image__GatsbyImageWrapper"] {
      padding-top: 56.25% !important;
    }
  }
`

const StyledImageOrVideo = styled(ImageOrVideo)`
  ${({ mediaPositioningNotFloating, theme }) =>
    mediaPositioningNotFloating &&
    css`
      .gatsby-image-wrapper,
      [class^="Image__GatsbyImageWrapper"] > div {
        @media (min-width: ${theme.breakpoints.md}) {
          padding-bottom: 31% !important;
        }
      }
    `}

  ${({ useFitMedia, fitMediaAlign, theme }) =>
    useFitMedia &&
    css`
      @media (min-width: ${theme.breakpoints.md}) and (max-width: calc(${theme
          .breakpoints.xl} - 0.02px)) {
        width: 100%;
        height: 100%;

        .gatsby-image-wrapper,
        [class^="Image__GatsbyImageWrapper"] {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          overflow: hidden !important;
        }

        .gatsby-image-wrapper > div,
        [class^="Image__GatsbyImageWrapper"] > div {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          padding-bottom: 0 !important;
        }

        .gatsby-image-wrapper picture,
        [class^="Image__GatsbyImageWrapper"] picture {
          width: 100% !important;
          height: 100% !important;
        }

        .gatsby-image-wrapper img,
        [class^="Image__GatsbyImageWrapper"] img,
        img.desktop-image,
        img.mobile-image,
        video,
        iframe {
          width: auto !important;
          min-width: 100% !important;
          max-width: none !important;
          height: 100% !important;
          min-height: 100% !important;
          object-fit: contain !important;
          object-position: ${fitMediaAlign} !important;
        }
      }
    `}

  ${({ useCropMedia, cropMediaAlign, theme }) =>
    useCropMedia &&
    css`
      @media (min-width: ${theme.breakpoints.md}) {
        width: 100%;
        height: 100%;

        .gatsby-image-wrapper,
        [class^="Image__GatsbyImageWrapper"] {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
        }

        .gatsby-image-wrapper > div,
        [class^="Image__GatsbyImageWrapper"] > div {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          padding-bottom: 0 !important;
        }

        .gatsby-image-wrapper picture,
        [class^="Image__GatsbyImageWrapper"] picture {
          width: 100% !important;
          height: 100% !important;
        }

        .gatsby-image-wrapper img,
        [class^="Image__GatsbyImageWrapper"] img,
        img.desktop-image,
        img.mobile-image,
        video,
        iframe {
          width: 100% !important;
          min-width: 100% !important;
          height: 100% !important;
          min-height: 100% !important;
          max-width: none !important;
          object-fit: cover !important;
          object-position: ${cropMediaAlign} !important;
        }
      }
    `}

  /* apply 16:9 aspect ratio for images in mobile breakpoint (425px) */
  ${props =>
    props.isMobileImage16By9AspectRatio
      ? set16By9AspectRatioForBreakpoint(breakpoints[0])
      : null}
`

// converts alignment (left/right) for LayoutChanger into expected values (start|center|end)
const toFlexAlign = alignValue =>
  alignValue === "left"
    ? "start"
    : alignValue === "right"
      ? "end"
      : alignValue || "start"

const getTextOnlyJustifyContent = alignValue => {
  if (typeof alignValue !== "string") return "center"

  const normalizedAlign = alignValue.toLowerCase()

  if (normalizedAlign === "start") return "flex-start"
  if (normalizedAlign === "end") return "flex-end"

  return "center"
}

// keeps StandardPadding horizontal alignment consistent with LayoutChanger alignment
const getStandardPaddingAlignProps = alignValue => {
  if (typeof alignValue !== "string") return {}

  const normalizedAlign = alignValue.toLowerCase()

  if (normalizedAlign === "start") return { mx: 0 } // left
  if (normalizedAlign === "end") return { ml: "auto", mr: 0 } // right

  return { mx: "auto" } // center
}

const getFitMediaAlign = locationValue =>
  locationValue === "left"
    ? "right center"
    : locationValue === "right"
      ? "left center"
      : "center"

const getCropMediaAlign = locationValue =>
  locationValue === "left"
    ? "right center"
    : locationValue === "right"
      ? "left center"
      : "center"

const getTextOnlyContentMaxWidth = titleMaxWidth => {
  if (titleMaxWidth === "fullWidth") return "100%"
  if (titleMaxWidth === "wide") return 56

  return 42
}

const ImageTextMolecule = ({
  align, // (left|center|right) or (start|center|end) depending on caller
  card = {},
  child1Px,
  imageOrVideo,
  layoutStyle,
  location,
  mediaAlignment,
  mediaLocation,
  mediaPositioning,
  mediaPositioningOnMobile,
  mediaRatio,
  mediaWidth,
  section,
  text,
  textChild,
  xOffsetOverlap,
  yOffsetOverlap,
  child2Style,
  child1Style,
  layoutChangerStyles,
  reverse,
  marginBtwChilds,
  mobileMarginBtwChilds,
  overlapOnMobile = true,
  standardPaddingStyles,
  isMobileImage16By9AspectRatio = false,
  isCarouselWithControls = false,
  imageFieldPrefix = null, // only used for flex columns
  useTextOnlyTitleMaxWidth = false,
  eagerLoad,
  ...rest
}) => {
  marginBtwChilds = marginBtwChilds ?? 48
  mobileMarginBtwChilds = mobileMarginBtwChilds ?? 32
  mediaPositioning = mediaPositioning === null ? "floating" : mediaPositioning

  const { navHeight, isTransparentNav, isFirstSection } = section

  const layoutType =
    location === "bottom" || location === "top" ? "stacked" : "adjacent"

  const hasImageOrVideo = hasRenderableMedia(imageOrVideo)

  const isMediaPositioningFloating =
    mediaPositioning && mediaPositioning === "floating"
  const isFitMediaWithMedia = mediaPositioning === "fit" && hasImageOrVideo
  const isCropMediaWithMedia = mediaPositioning === "crop" && hasImageOrVideo
  const isFullHeightMediaWithMedia = isFitMediaWithMedia || isCropMediaWithMedia
  const shouldUseAbsoluteFitMedia =
    isFitMediaWithMedia && layoutType === "adjacent"
  const shouldUseAbsoluteCropMedia =
    isCropMediaWithMedia && layoutType === "adjacent"
  const shouldUseAbsoluteMedia =
    shouldUseAbsoluteFitMedia || shouldUseAbsoluteCropMedia
  const shouldCenterNonFloatingAdjacentLayout =
    hasImageOrVideo && !isMediaPositioningFloating && layoutType === "adjacent"
  const fitMediaAlign = getFitMediaAlign(location)
  const cropMediaAlign = getCropMediaAlign(location)
  const mediaAlignProp =
    layoutStyle === "overlapping"
      ? layoutType === "stacked"
        ? "center"
        : "start"
      : toFlexAlign(mediaAlignment ?? "center")

  // limit xOffsetOverlap based on layout settings
  xOffsetOverlap =
    layoutType === "stacked"
      ? (text?.titleMaxWidth === "fullWidth" ||
          text?.titleMaxWidth === "wide") &&
        xOffsetOverlap >= 200
        ? 200
        : xOffsetOverlap >= 383
          ? 383
          : xOffsetOverlap <= -400
            ? -400
            : xOffsetOverlap
      : xOffsetOverlap // limiting xOffsetOverlap based on layout settings

  const overlap = layoutStyle === "overlapping"
  mediaRatio = mediaRatio === null ? 50 : mediaRatio // default mediaRatio to 50 if not provided

  const alignProp =
    overlap && layoutType === "stacked"
      ? "end"
      : overlap && layoutType === "adjacent" && mediaPositioning !== "floating"
        ? "center"
        : overlap && layoutType === "adjacent"
          ? "start"
          : shouldCenterNonFloatingAdjacentLayout
            ? "center"
            : toFlexAlign(align) // keep start|center|end as-is; translate left/right only

  section.verticalPadding =
    section.verticalPadding === null
      ? "default"
      : overlap && hasImageOrVideo && !isMediaPositioningFloating
        ? "md" // defaulting verticalPadding to overlap and non-floating layout
        : section.verticalPadding

  const hasTextChild = Boolean(textChild)
  const isTextOnly = hasTextChild && !hasImageOrVideo

  if (!hasTextChild && !hasImageOrVideo) return null

  const shouldUseTextJustificationForAlign =
    isTextOnly && (mediaPositioning === "fit" || mediaPositioning === "crop")
  const textOnlyAlignProp = isTextOnly
    ? toFlexAlign(shouldUseTextJustificationForAlign ? text?.align : align)
    : alignProp

  // Use default 1024px for old Image Text sections have an unset or smaller maxWidth than 1024px
  const resolvedSectionMaxWidth =
    typeof section?.maxWidth === "number" && section.maxWidth >= 1024
      ? section.maxWidth
      : 1024

  // Keeps fit/crop stacked layout aligned with the Alignment field
  const fullHeightMediaStackStandardPaddingAlignProps =
    isFullHeightMediaWithMedia && layoutType === "stacked"
      ? getStandardPaddingAlignProps(alignProp)
      : {}

  // set full dimension for fit/crop mediaPositionining
  const imageOrVideoStyles = !isMediaPositioningFloating
    ? {
        width: "100%",
        height: "100%",
      }
    : { ...imageOrVideo?.imageStyles }

  const customPadding = {
    _: `${section.mobileCustomVerticalPadding ?? section.customVerticalPadding ?? 0}px`,
    md: `${section.customVerticalPadding ?? 0}px`,
  }

  const getFullHeightMediaPaddingProps = () => {
    if (section.verticalPadding === "custom") {
      if (section.spacingLinked) {
        return {
          py: {
            _: `${section.mobileCustomVerticalPadding ?? section.customVerticalPadding ?? 0}px`,
            md: `${section.customVerticalPadding ?? 0}px`,
          },
        }
      }

      return {
        pt: {
          _: `${section.mobileCustomSpacingTop ?? section.customSpacingTop ?? 0}px`,
          md: `${section.customSpacingTop ?? 0}px`,
        },
        pb: {
          _: `${section.mobileCustomSpacingBottom ?? section.customSpacingBottom ?? 0}px`,
          md: `${section.customSpacingBottom ?? 0}px`,
        },
      }
    }

    return {
      verticalPadding: section?.verticalPadding ?? "md",
    }
  }

  let child1StyleProp = {
    alignSelf: alignProp === "center" ? "center" : `flex-${alignProp}`,
    width: layoutType === "adjacent" ? "100%" : "inherit",
    zIndex: overlap ? 15 : 10,
    flex: 1,
    minWidth:
      hasImageOrVideo && layoutType === "adjacent" && mediaRatio > 50
        ? { _: "100%", md: "0%" }
        : null,
  }

  let child2StyleProp = {
    zIndex: 12,
    alignSelf: !isMediaPositioningFloating
      ? { _: "center", sm: "stretch" }
      : { _: overlap ? "end" : "center", md: mediaAlignProp },
    width: "100%",
    flex: mediaRatio ? mediaRatio / (100 - mediaRatio) : 1,
    minWidth: layoutType === "adjacent" ? 0 : null,
    minHeight: shouldUseAbsoluteMedia ? { _: null, md: 0 } : null,
  }

  let conditionalProps = {
    py: {
      _: 0, // prevent unwanted spacing between image and text on mobile
      md: !overlap
        ? !isMediaPositioningFloating && section.verticalPadding === "custom"
          ? customPadding.md
          : !isMediaPositioningFloating && section.verticalPadding === "md"
            ? 9
            : (!isMediaPositioningFloating &&
                  section.verticalPadding === "lg") ||
                (!isMediaPositioningFloating &&
                  section.verticalPadding === "fullHeight")
              ? 12
              : null
        : overlap
          ? !isMediaPositioningFloating
            ? 0
            : null
          : null,
    },
    verticalPadding: "custom",
    position: "relative",
    zIndex: 10,
    width: "100%",
    height: "100%",
  }

  let imageOrVideoBoxStyles = {}

  if (mediaPositioning === "floating") {
    conditionalProps.px = { _: 5, md: 0 }
    if (location === "left") {
      child2StyleProp.ml = { _: 0, md: 7 }
      child2StyleProp.mr = { _: 0 }
    } else if (location === "right") {
      child2StyleProp.mr = { _: 0, md: 7 }
      child2StyleProp.ml = { _: 0 }
    }
  } else if (isFullHeightMediaWithMedia) {
    conditionalProps.px = { _: 5, md: 0 } // horizontal padding needed when fit/crop and stacked on mobile
    if (location === "right") {
      conditionalProps.pr = { _: 5, md: 7, xl: 8 }
    } else if (location === "left") {
      conditionalProps.pl = { _: 5, md: 7, xl: 8 }
    }
  }

  //  Overlap settings
  let yOffsetOverlapPixels = Math.abs(yOffsetOverlap)
  if (yOffsetOverlapPixels > 340 && layoutType === "stacked") {
    yOffsetOverlapPixels = 340
  }

  // forcing a card for child1 if overlapping is set
  card = {
    ...card,
    useCard: overlap && !card?.useCard ? true : card?.useCard,
    theme: overlap && !card?.useCard ? "light" : card?.theme,
    bg: overlap && !card?.useCard ? 0 : card?.bg,
  }

  const shouldDisabledXOffset =
    text?.titleMaxWidth === "fullWidth" && layoutType === "stacked" // disabling x-offset to prevent x-axis cut off

  // ------------------------------ Mobile Padding / Vertical Spacing ------------------------------
  const stdPaddingForNonFloatingLayout = {
    _:
      section.verticalPadding === "lg"
        ? 11
        : section.verticalPadding === "default" ||
            section.verticalPadding === "md"
          ? 7
          : section.verticalPadding === "sm"
            ? 5
            : section.verticalPadding === "fullHeight"
              ? 7
              : customPadding._,
  }

  let layoutChangerStyleProps = { ...layoutChangerStyles }
  // If not floating, control child2StyleProp.mt on mobile
  if (!isMediaPositioningFloating) {
    child2StyleProp.mt = {
      _: overlap && mediaPositioningOnMobile === "top" ? 7 : "0px", // setting margin top to zero pixel for media on mobile layout
    }

    if (mediaPositioningOnMobile === "bottom") {
      if (!overlap) {
        conditionalProps.pt = stdPaddingForNonFloatingLayout
        conditionalProps.pb = {
          _: `${mobileMarginBtwChilds}px`,
          md:
            section.verticalPadding === "custom"
              ? customPadding.md
              : stdPaddingForNonFloatingLayout._,
        } // limiting space between image and text to marginBtwChilds spacing
      }
      if (isCarouselWithControls) {
        imageOrVideoBoxStyles.pb = { ...stdPaddingForNonFloatingLayout, md: 0 }
      }
      if (isTransparentNav && isFirstSection && hasImageOrVideo) {
        layoutChangerStyleProps.mt = { _: 0, md: `${-navHeight}px` }
      }
    }

    if (mediaPositioningOnMobile === "top") {
      if (!hasImageOrVideo) {
        // Adding padding top when there's NO image/video to mimic standard padding behavior
        conditionalProps.pt = {
          ...stdPaddingForNonFloatingLayout,
        }
      } else {
        conditionalProps.pt = 0
        if (isTransparentNav && isFirstSection) {
          layoutChangerStyleProps.mt = `${-navHeight}px`
        }
      }
      conditionalProps.pb = {
        ...stdPaddingForNonFloatingLayout,
      }
    }
  }

  // Fit and crop move section spacing to the text container.
  if (isFullHeightMediaWithMedia) {
    const fullHeightMediaPaddingProps = getFullHeightMediaPaddingProps()

    delete conditionalProps.py
    delete conditionalProps.pt
    delete conditionalProps.pb
    delete conditionalProps.height
    delete conditionalProps.verticalPadding

    conditionalProps = {
      ...conditionalProps,
      ...fullHeightMediaPaddingProps,
    }
  }
  // --------------------------------------------------------------------------------

  if (mediaPositioning !== "floating") {
    // marginBtwChilds is handled by LayoutChanger when not floating
    child2StyleProp.ml = 0
    child2StyleProp.mr = 0
  }

  // Overlap logic
  if (hasImageOrVideo && overlap) {
    if (mediaPositioning === "floating") {
      child2StyleProp.ml = 0 // setting x-offset to zero pixel between image and text when x-offset is zero
      child2StyleProp.mr = 0 // setting x-offset to zero pixel between image and text when x-offset is zero
      child1StyleProp.mt = {
        _: !overlapOnMobile
          ? `${marginBtwChilds}px`
          : mediaPositioningOnMobile === "bottom"
            ? 0
            : yOffsetOverlap > 56
              ? "56px"
              : `${-yOffsetOverlapPixels * 0.25}px`,
        sm:
          mediaPositioningOnMobile === "bottom"
            ? 0
            : yOffsetOverlap > 56
              ? "56px"
              : `${yOffsetOverlap * 0.5}px`,
        md: `${
          layoutType === "adjacent" && yOffsetOverlap > 0
            ? yOffsetOverlapPixels // this margin needs to be positive to prevent card overflowing to sectionTitle
            : location === "top" ||
                (layoutType === "adjacent" && yOffsetOverlap <= 0)
              ? 0
              : yOffsetOverlap < -56
                ? "56px" // limiting margin between image and text to at most 56px
                : yOffsetOverlap < 0
                  ? yOffsetOverlapPixels
                  : -yOffsetOverlapPixels
        }px`,
      }

      // child2 offsets
      child2StyleProp.mt = {
        _: !overlapOnMobile
          ? `${marginBtwChilds}px`
          : mediaPositioningOnMobile === "bottom"
            ? `${-yOffsetOverlapPixels * 0.15}px`
            : 0,
        md: "0px", // setting y-offset to zero pixel between image and text when y-offset is zero
      }
    }

    if (!isMediaPositioningFloating) {
      child1StyleProp.mt = {
        _: !overlapOnMobile
          ? `${marginBtwChilds}px`
          : mediaPositioningOnMobile === "bottom"
            ? 0
            : yOffsetOverlap > 56
              ? "-56px"
              : `${-yOffsetOverlapPixels * 0.25}px`, // overlapping both elements on mobile
        sm:
          mediaPositioningOnMobile === "top"
            ? `${-yOffsetOverlapPixels * 0.25}px`
            : 0, // mediaPositioning fit/crop layout turns into a stack layout on mobile
        md: yOffsetOverlap > 0 ? `${yOffsetOverlapPixels * 0.25}px` : 0, // margin needs to be positioned on desktop layout to prevent overflow
        lg: yOffsetOverlap > 0 ? `${yOffsetOverlapPixels}px` : 0, // margin needs to be positioned on desktop layout to prevent overflow
      }
    }
    //------------------------ x-offset overlapping logic ------------------------

    // x-offset overlapping logic
    if (!shouldDisabledXOffset) {
      child1StyleProp.position = "relative"
      child1StyleProp.left =
        location === "left" && !isMediaPositioningFloating
          ? {
              _: "0px",
              sm: !overlapOnMobile
                ? "0px"
                : mediaLocation === "left"
                  ? "24px"
                  : "-24px",
              md: `${xOffsetOverlap}px`,
            }
          : location === "left" && mediaPositioning === "floating"
            ? {
                _: "0px",
                sm: !overlapOnMobile
                  ? "0px"
                  : mediaLocation === "left"
                    ? "24px"
                    : "-24px",
                md: `${xOffsetOverlap}px`,
              }
            : location === "right" && !isMediaPositioningFloating
              ? {
                  _: "0px",
                  sm: !overlapOnMobile
                    ? "0px"
                    : mediaLocation === "left"
                      ? "24px"
                      : "-24px",
                  md: `-${Math.abs(xOffsetOverlap)}px`,
                }
              : (location === "right" && mediaPositioning === "floating") ||
                  layoutType === "stacked"
                ? {
                    _: "0px",
                    sm: !overlapOnMobile
                      ? "0px"
                      : mediaLocation === "left"
                        ? "24px"
                        : "-24px",
                    md: `-${Math.abs(xOffsetOverlap * 0.2)}px`,
                    lg: `-${Math.abs(xOffsetOverlap * 0.45)}px`,
                    xl: `-${Math.abs(xOffsetOverlap)}px`,
                  } // using pixel values here since spacing constants did not work
                : null
    }
    //--------------------------------------------------------------------------------------------

    if (mediaPositioning === "floating") {
      child2StyleProp.mr = {
        _: !overlapOnMobile ? 0 : mediaLocation === "right" ? "-5px" : "5px",
        md: 0,
      }

      if (layoutType === "adjacent" && yOffsetOverlap < 0) {
        child2StyleProp.mt = {
          _:
            mediaPositioningOnMobile === "bottom"
              ? `${-yOffsetOverlapPixels * 0.15}px`
              : 0,
          md: `${yOffsetOverlapPixels}px`,
        }
      }

      if (layoutType === "stacked") {
        child1StyleProp.mb = {
          _:
            mediaPositioningOnMobile === "top"
              ? 0
              : yOffsetOverlap > 56
                ? "56px"
                : `${yOffsetOverlap * 0.25}px`,
          sm:
            yOffsetOverlap > 56
              ? "56px"
              : location === "bottom" && mediaPositioningOnMobile === "bottom"
                ? `${-yOffsetOverlapPixels * 0.15}px`
                : mediaPositioningOnMobile === "top"
                  ? 0
                  : `${yOffsetOverlap * 0.7}px`,
          md:
            yOffsetOverlap > 56
              ? "56px"
              : location === "top"
                ? `${
                    yOffsetOverlap < 0
                      ? -yOffsetOverlapPixels
                      : yOffsetOverlapPixels
                  }px`
                : null,
        }
      }
    }

    if (!isMediaPositioningFloating) {
      child1StyleProp.mb = {
        _:
          mediaPositioningOnMobile === "top"
            ? 0
            : `${-yOffsetOverlapPixels * 0.15}px`,
        sm:
          mediaPositioningOnMobile === "top"
            ? 0
            : `${-yOffsetOverlapPixels * 0.25}px`,
        md: yOffsetOverlap < 0 ? `${yOffsetOverlapPixels}px` : 0,
      }
    }
  }

  if (hasImageOrVideo && overlap && mediaPositioning !== "floating") {
    // these padding should be combined with the conditionalProps variable.
    // and the hardcoded 128 should be replaced with py value
    child1StyleProp.pb = {
      _: mediaPositioningOnMobile === "bottom" ? `${marginBtwChilds}px` : 0,
      md: yOffsetOverlapPixels < 128 ? `${128 - yOffsetOverlapPixels}px` : 0,
    }

    child1StyleProp.pt = {
      _: `${marginBtwChilds}px`,
      md:
        yOffsetOverlap < 0 && yOffsetOverlapPixels < 128
          ? yOffsetOverlapPixels - 128 > 0
            ? `${yOffsetOverlapPixels - 128}px`
            : 0
          : 0,
    }
  }

  // textBox width adjustments
  const textBoxStyles = {}
  if (isTextOnly && useTextOnlyTitleMaxWidth) {
    textBoxStyles.width = "100%"
  }

  if (hasImageOrVideo && layoutType === "adjacent") {
    if (!isMediaPositioningFloating) {
      if (overlap) {
        if (text?.titleMaxWidth === "normal") {
          textBoxStyles.width = { _: "100%", md: `${50 + mediaRatio}%` }
        } else if (text?.titleMaxWidth === "wide") {
          textBoxStyles.width = { _: "100%", md: `${80 + mediaRatio}%` }
        } else if (text?.titleMaxWidth === "fullWidth") {
          textBoxStyles.width = { _: "100%", md: `${110 + mediaRatio}%` }
        }
      } else if (mediaRatio === null) {
        if (text?.titleMaxWidth === "normal") {
          textBoxStyles.width = { _: "100%", md: "100%" }
        } else if (text?.titleMaxWidth === "wide") {
          textBoxStyles.width = { _: "100%", md: 46 }
        } else if (text?.titleMaxWidth === "fullWidth") {
          textBoxStyles.width = { _: "100%", md: 56 }
        }
      }
    } else {
      textBoxStyles.width = { _: "100%", md: "100%" }
    }
  }

  child1StyleProp = { ...child1StyleProp, ...child1Style }
  child2StyleProp = { ...child2StyleProp, ...child2Style }

  // Keeps fit/crop padding self-contained so preset and custom spacing come from the same source.
  const baseStandardPaddingStyles = isFullHeightMediaWithMedia
    ? conditionalProps
    : {
        ...conditionalProps,
        ...standardPaddingStyles,
      }

  const shouldUseInnerStandardPadding = isFullHeightMediaWithMedia
  const shouldKeepEmptyTextColumn =
    layoutType === "adjacent" && hasImageOrVideo && !hasTextChild
  const shouldRenderFullHeightMediaSpacer =
    isFullHeightMediaWithMedia &&
    layoutType === "adjacent" &&
    hasImageOrVideo &&
    !hasTextChild

  const textBoxContent = (
    <Box className="textBoxStyles" {...textBoxStyles}>
      {textChild}
    </Box>
  )

  const textOnlyContentMaxWidth = useTextOnlyTitleMaxWidth
    ? getTextOnlyContentMaxWidth(text?.titleMaxWidth)
    : resolvedSectionMaxWidth
  const textRevealHeight = isFullHeightMediaWithMedia ? undefined : "100%"
  const textRevealWidth =
    isTextOnly || layoutType === "adjacent" ? "100%" : undefined

  const textContent = hasTextChild ? (
    <Reveal height={textRevealHeight} width={textRevealWidth}>
      {isTextOnly ? (
        <Box
          width="100%"
          display="flex"
          justifyContent={getTextOnlyJustifyContent(textOnlyAlignProp)}
        >
          <Box width="100%" maxWidth={textOnlyContentMaxWidth} flexShrink={1}>
            {textBoxContent}
          </Box>
        </Box>
      ) : shouldUseInnerStandardPadding ? (
        <StandardPadding
          maxWidth={resolvedSectionMaxWidth}
          {...baseStandardPaddingStyles}
          {...fullHeightMediaStackStandardPaddingAlignProps}
        >
          {textBoxContent}
        </StandardPadding>
      ) : (
        textBoxContent
      )}
    </Reveal>
  ) : shouldRenderFullHeightMediaSpacer ? (
    <StandardPadding
      maxWidth={resolvedSectionMaxWidth}
      {...baseStandardPaddingStyles}
      {...fullHeightMediaStackStandardPaddingAlignProps}
    >
      <Box width="100%" height="100%" aria-hidden="true" />
    </StandardPadding>
  ) : shouldKeepEmptyTextColumn ? (
    <Box width="100%" height="100%" aria-hidden="true" />
  ) : null

  const fitMediaContent = hasImageOrVideo ? (
    <Box
      position="relative"
      width="100%"
      display="flex"
      justifyContent={
        location === "right"
          ? "flex-end"
          : location === "left"
            ? "flex-start"
            : "center"
      }
      alignItems="stretch"
      height={{ _: "auto", md: "100%", xl: "auto" }}
      minHeight={
        shouldUseAbsoluteFitMedia ? { _: "auto", md: 0, xl: "auto" } : undefined
      }
      {...imageOrVideoBoxStyles}
    >
      <Box
        position={
          shouldUseAbsoluteFitMedia
            ? { _: "relative", md: "absolute", xl: "relative" }
            : "relative"
        }
        top={
          shouldUseAbsoluteFitMedia
            ? { _: "auto", md: 0, xl: "auto" }
            : undefined
        }
        right={
          shouldUseAbsoluteFitMedia
            ? { _: "auto", md: 0, xl: "auto" }
            : undefined
        }
        bottom={
          shouldUseAbsoluteFitMedia
            ? { _: "auto", md: 0, xl: "auto" }
            : undefined
        }
        left={
          shouldUseAbsoluteFitMedia
            ? { _: "auto", md: 0, xl: "auto" }
            : undefined
        }
        height={{ _: "auto", md: "100%", xl: "auto" }}
        width="100%"
        maxWidth={
          layoutType === "adjacent"
            ? "100%"
            : mediaWidth !== null
              ? `${mediaWidth}%`
              : 44
        }
        overflow={
          layoutType === "adjacent"
            ? { _: "visible", md: "hidden", xl: "visible" }
            : undefined
        }
      >
        <Reveal height="100%" width="100%">
          <StyledImageOrVideo
            videoProps={{
              ...imageOrVideo?.videoProps,
              ...imageOrVideoStyles,
            }}
            imageStyles={imageOrVideoStyles}
            mediaPositioningNotFloating={!isMediaPositioningFloating && overlap}
            useFitMedia={isFitMediaWithMedia && layoutType === "adjacent"}
            fitMediaAlign={fitMediaAlign}
            isMobileImage16By9AspectRatio={isMobileImage16By9AspectRatio}
            imageFieldId={
              imageFieldPrefix
                ? `${imageFieldPrefix}.imageOrVideo.desktopImage`
                : `${section.sectionId}.imageOrVideo.desktopImage`
            }
            {...imageOrVideo}
            eagerLoad={eagerLoad} // passing eagerLoad so hero images load immediately
          />
        </Reveal>
      </Box>
    </Box>
  ) : null

  // Crop uses the same full-height media wrapper path as fit.
  // Keep it relative on mobile so media still renders before the layout becomes side-by-side.
  const cropMediaContent = hasImageOrVideo ? (
    <Box
      position="relative"
      width="100%"
      display="flex"
      justifyContent={
        !isMediaPositioningFloating && location === "right"
          ? "flex-end"
          : !isMediaPositioningFloating && location === "left"
            ? "flex-start"
            : "center"
      }
      height={{ _: "auto", md: "100%" }}
      minHeight={shouldUseAbsoluteCropMedia ? { _: "auto", md: 0 } : undefined}
      {...imageOrVideoBoxStyles}
    >
      <Box
        position={
          shouldUseAbsoluteCropMedia
            ? { _: "relative", md: "absolute" }
            : "relative"
        }
        top={shouldUseAbsoluteCropMedia ? { _: "auto", md: 0 } : undefined}
        right={shouldUseAbsoluteCropMedia ? { _: "auto", md: 0 } : undefined}
        bottom={shouldUseAbsoluteCropMedia ? { _: "auto", md: 0 } : undefined}
        left={shouldUseAbsoluteCropMedia ? { _: "auto", md: 0 } : undefined}
        height={{ _: "auto", md: "100%" }}
        width="100%"
        maxWidth={
          layoutType === "adjacent"
            ? "100%"
            : mediaWidth !== null
              ? `${mediaWidth}%`
              : 44
        }
        overflow={
          layoutType === "adjacent" ? { _: "visible", md: "hidden" } : undefined
        }
      >
        <Reveal height="100%" width="100%">
          <StyledImageOrVideo
            videoProps={{
              ...imageOrVideo?.videoProps,
              ...imageOrVideoStyles,
            }}
            imageStyles={imageOrVideoStyles}
            mediaPositioningNotFloating={!isMediaPositioningFloating && overlap}
            useCropMedia={isCropMediaWithMedia}
            cropMediaAlign={cropMediaAlign}
            isMobileImage16By9AspectRatio={isMobileImage16By9AspectRatio}
            imageFieldId={
              imageFieldPrefix
                ? `${imageFieldPrefix}.imageOrVideo.desktopImage`
                : `${section.sectionId}.imageOrVideo.desktopImage`
            }
            {...imageOrVideo}
            eagerLoad={eagerLoad} // passing eagerLoad so hero images load immediately
          />
        </Reveal>
      </Box>
    </Box>
  ) : null

  const floatingMediaJustifyContent =
    layoutType === "stacked"
      ? mediaAlignProp === "start"
        ? "flex-start"
        : mediaAlignProp === "end"
          ? "flex-end"
          : "center"
      : !isMediaPositioningFloating && location === "right"
        ? "flex-end"
        : !isMediaPositioningFloating && location === "left"
          ? "flex-start"
          : "center"

  const floatingMediaContent = (
    <Box
      position="relative"
      width="100%"
      display="flex"
      justifyContent={floatingMediaJustifyContent}
      height="auto"
      {...imageOrVideoBoxStyles}
    >
      <Box
        position="relative"
        height="100%"
        width="100%"
        maxWidth={
          layoutType === "adjacent"
            ? "100%"
            : mediaWidth !== null
              ? `${mediaWidth}%`
              : 44
        }
      >
        <Reveal height="100%" width="100%">
          <StyledImageOrVideo
            videoProps={{
              ...imageOrVideo?.videoProps,
              ...imageOrVideoStyles,
            }}
            imageStyles={imageOrVideoStyles}
            mediaPositioningNotFloating={!isMediaPositioningFloating && overlap}
            isMobileImage16By9AspectRatio={isMobileImage16By9AspectRatio}
            imageFieldId={
              imageFieldPrefix
                ? `${imageFieldPrefix}.imageOrVideo.desktopImage`
                : `${section.sectionId}.imageOrVideo.desktopImage`
            }
            {...imageOrVideo}
            eagerLoad={eagerLoad} // passing eagerLoad so hero images load immediately
          />
        </Reveal>
      </Box>
    </Box>
  )

  const mediaContent = hasImageOrVideo
    ? isCropMediaWithMedia
      ? cropMediaContent
      : isFitMediaWithMedia
        ? fitMediaContent
        : floatingMediaContent
    : null

  if (isTextOnly) {
    return (
      <Box width="100%" {...layoutChangerStyleProps}>
        {textContent}
      </Box>
    )
  }

  const layoutChangerAlign = layoutType === "adjacent" ? "stretch" : alignProp

  return (
    <LayoutChanger
      itemsMoveStackedOnColumn={overlap ? false : true}
      disableMaxWidth={layoutType === "stacked" && mediaWidth !== null}
      child1Style={{
        ...child1Style,
        ...child1StyleProp,
      }}
      child2Style={{
        ...child2Style,
        ...child2StyleProp,
      }}
      location={location}
      child1Px={child1Px}
      align={layoutChangerAlign}
      marginBtwChilds={
        overlap && !isMediaPositioningFloating && card.useCard
          ? 10
          : !isMediaPositioningFloating && overlap
            ? 0
            : marginBtwChilds
      }
      mobileMarginBtwChilds={
        overlap && !isMediaPositioningFloating && card.useCard
          ? 10
          : !isMediaPositioningFloating && overlap
            ? 0
            : mobileMarginBtwChilds
      }
      isMarginBtwChildsAPixelValue={true}
      reverse={reverse ? reverse : true}
      child1FlexDirOnMobile={
        mediaPositioningOnMobile === "top" ? "column-reverse" : "column"
      }
      {...layoutChangerStyleProps}
    >
      {textContent}
      {mediaContent}
    </LayoutChanger>
  )
}

export default ImageTextMolecule
// export const query = graphql`
//   fragment ImageTextMolecule on STRAPI__COMPONENT_MOLECULES_IMAGE_TEXT_MOLECULE {
//     id
//     align
//     card
//     child1Px
//     imageOrVideo
//     imageTextAlign
//     imageTextLocation
//     layoutStyle
//     location
//     mediaLocation
//     mediaPositioning
//     mediaPositioningOnMobile
//     mediaRatio
//     section
//     socialLinkStyles
//     text
//     xOffsetOverlap
//     yOffsetOverlap
//     textChild
//   }
// `
